/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import models.*

/**
 * Stub implementation of DatabaseRepository for initial build
 * This provides basic no-op implementations to get the project building
 */
class DatabaseRepositoryStub : DatabaseRepository {
    
    // Transaction operations - all return empty/default results
    override suspend fun insertTransaction(transaction: Transaction): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun insertTransactions(transactions: List<Transaction>): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getTransactionById(transactionId: String): DatabaseResult<Transaction?> = DatabaseResult.Success(null)
    override suspend fun getTransactions(filter: TransactionFilter, sortOrder: TransactionSortOrder, limit: Long, offset: Long): DatabaseResult<PagedResult<Transaction>> = 
        DatabaseResult.Success(PagedResult(emptyList(), 0, false, offset, limit))
    override suspend fun getTransactionsByAccount(accountId: String, limit: Long, offset: Long): DatabaseResult<List<Transaction>> = DatabaseResult.Success(emptyList())
    override suspend fun getRecentTransactions(userId: String, limit: Long): DatabaseResult<List<Transaction>> = DatabaseResult.Success(emptyList())
    override suspend fun getPendingTransactions(userId: String): DatabaseResult<List<Transaction>> = DatabaseResult.Success(emptyList())
    override suspend fun searchTransactions(userId: String, query: String, limit: Long): DatabaseResult<List<Transaction>> = DatabaseResult.Success(emptyList())
    override suspend fun updateTransaction(transaction: Transaction): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun updateTransactionUserData(transactionId: String, userCategory: String?, userNotes: String?, tags: List<String>?, isHidden: Boolean?, isExpenseReport: Boolean?): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun deleteTransaction(transactionId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun deleteTransactionsByAccount(accountId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getTransactionCount(userId: String): DatabaseResult<Long> = DatabaseResult.Success(0)
    
    // Account operations - all return empty/default results
    override suspend fun insertAccount(account: LocalAccount): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun insertAccounts(accounts: List<LocalAccount>): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getAccountById(accountId: String): DatabaseResult<LocalAccount?> = DatabaseResult.Success(null)
    override suspend fun getAccountsByUser(filter: AccountFilter): DatabaseResult<List<LocalAccount>> = DatabaseResult.Success(emptyList())
    override suspend fun getAccountsByItem(itemId: String): DatabaseResult<List<LocalAccount>> = DatabaseResult.Success(emptyList())
    override suspend fun updateAccountBalance(accountId: String, availableBalance: Double?, currentBalance: Double?, creditLimit: Double?, lastUpdated: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun updateAccount(account: LocalAccount): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun deleteAccount(accountId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun deleteAccountsByItem(itemId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    
    // Plaid token operations - all return empty/default results
    override suspend fun insertPlaidToken(token: LocalPlaidToken): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getPlaidTokenByItemId(itemId: String): DatabaseResult<LocalPlaidToken?> = DatabaseResult.Success(null)
    override suspend fun getPlaidTokensByUser(userId: String): DatabaseResult<List<LocalPlaidToken>> = DatabaseResult.Success(emptyList())
    override suspend fun getActivePlaidTokens(userId: String): DatabaseResult<List<LocalPlaidToken>> = DatabaseResult.Success(emptyList())
    override suspend fun updatePlaidTokenState(itemId: String, state: String, errorCode: String?, errorMessage: String?): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun markPlaidTokenUsed(itemId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun updatePlaidToken(token: LocalPlaidToken): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun deletePlaidToken(itemId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    
    // User operations - all return empty/default results
    override suspend fun upsertUser(userId: String, username: String, email: String?): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun updateUserLastLogin(userId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getUser(userId: String): DatabaseResult<Map<String, Any?>?> = DatabaseResult.Success(null)
    override suspend fun deleteUser(userId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    
    // Utility operations - all return default results
    override suspend fun <T> executeTransaction(block: suspend () -> T): DatabaseResult<T> {
        return try {
            DatabaseResult.Success(block())
        } catch (e: Exception) {
            DatabaseResult.Error(e)
        }
    }
    override suspend fun clearUserData(userId: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getDatabaseStats(): DatabaseResult<Map<String, Any>> = DatabaseResult.Success(emptyMap())
    override suspend fun optimizeDatabase(): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    
    // Sync operations - all return default results
    override suspend fun getLastSyncTime(itemId: String): DatabaseResult<String?> = DatabaseResult.Success(null)
    override suspend fun updateLastSyncTime(itemId: String, timestamp: String): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    override suspend fun getTransactionsPendingSync(userId: String): DatabaseResult<List<Transaction>> = DatabaseResult.Success(emptyList())
    override suspend fun markTransactionsAsSynced(transactionIds: List<String>): DatabaseResult<Unit> = DatabaseResult.Success(Unit)
    
    // TODO: Authentication operations will be implemented when auth service is restored
}